/**
 * @typedef {'UPL' | 'FACU' | 'FAC' | 'FACW' | 'OBL'} WetlandRating
 */

/**
 * @typedef {Object} PlantSpecies
 * @property {string} id - UUID
 * @property {string} commonName - Required
 * @property {string} scientificName - Required
 * @property {WetlandRating | null} agcpRating
 * @property {WetlandRating | null} empRating
 * @property {string} lightRequirements
 * @property {string} moistureRequirements
 * @property {string} soilRequirements
 * @property {string} bloomSeason
 * @property {string} bloomColor - Can be empty
 * @property {number|null} saltTolerance - 1-5 scale, null if unknown
 * @property {string} createdAt - ISO timestamp
 * @property {string} updatedAt - ISO timestamp
 */

/**
 * @typedef {Object} PlantRecord
 * @property {string} id - UUID
 * @property {string} speciesId - FK to PlantSpecies.id
 * @property {number} gridRow - Row position in grid
 * @property {number} gridCol - Column position in grid
 * @property {string} commonName - Denormalized from species for display
 * @property {string} scientificName - Denormalized from species for display
 * @property {WetlandRating | null} agcpRating - Denormalized from species
 * @property {WetlandRating | null} empRating - Denormalized from species
 * @property {string} source
 * @property {number} qtyPlugs
 * @property {number} qtySoloCups
 * @property {number} qtyQuarts
 * @property {number} qtyGallons
 * @property {string} lightRequirements - Denormalized from species
 * @property {string} moistureRequirements - Denormalized from species
 * @property {string} soilRequirements - Denormalized from species
 * @property {string} bloomSeason - Denormalized from species
 * @property {string} bloomColor - Denormalized from species
 * @property {string} trayNumber - Optional tray identifier
 * @property {string} createdAt - ISO timestamp
 * @property {string} updatedAt - ISO timestamp
 */

/**
 * @typedef {Object} LogEntry
 * @property {string} id - UUID
 * @property {string} plantId - FK to PlantRecord.id
 * @property {string} timestamp - ISO timestamp, auto-generated
 * @property {string} notes - Optional text
 * @property {string[]} photoIds - References to Photo records
 */

/**
 * @typedef {Object} Photo
 * @property {string} id - UUID
 * @property {Blob} blob - Image data
 * @property {string} mimeType
 * @property {string} createdAt - ISO timestamp
 */

/**
 * @typedef {Object} GridConfig
 * @property {string} id - Singleton record
 * @property {number} rows
 * @property {number} cols
 */

/** @type {WetlandRating[]} */
export const WETLAND_RATINGS = ['UPL', 'FACU', 'FAC', 'FACW', 'OBL'];
